import { useState, useEffect, useRef } from 'react';
import {
    Button,
    Container,
    DiscoverMore,
    PageBanner,
} from '~/Common';
import {
    Badge,
    Box,
    Flex,
    Grid,
    Heading,
    NavLink,
    Text
} from '@theme-ui/components';
import { Filters, CompanyCard } from '~/Industries';
import { Icon } from 'assets/Icon';
import { DashboardLayout } from '~/Layouts';
import { connect, useDispatch } from 'react-redux';
import { useTranslation } from 'react-i18next';
import { appendCategoriesAsync, getCategories, getCategoriesAsync } from 'features/category/categorySlice';
import { useDocumentTitle } from 'hooks/useDocumentTitle';
import CompaniesCarousel from '~/Industries/CompaniesCarousel/CompaniesCarousel';
import { getCompaniesAsync } from 'features/company/companySlice';

const mapStateToProps = (state) => {
    return { companies: state.companies, categories: state.categories }
}

const IndustriesDashboard = ({ categories, companies }) => {
    const defaultResults = new Array(9).fill(null);
    const { t } = useTranslation();
    useDocumentTitle(t('Industry Insights'));
    const dispatch = useDispatch();
    const categoriesRef = useRef(null);
    const [results, setResults] = useState(defaultResults);
    const [isFiltering, setIsFiltering] = useState(false);
    const [loading, setLoading] = useState(true);
    const [liveFilters, setLiveFilters] = useState({});
    const filterRef = useRef();

    const handleSubmit = (values) => {
        setIsFiltering(true);
        const payload = getCategories({
            params: {
                page: 0,
                search: values?.search ?? null
            }
        });
        setLiveFilters({
            page: 0,
            search: values?.search ?? null
        });
        setLoading(true);
        dispatch(getCategoriesAsync(payload));
        return false;
    };

    const resetFilters = (e) => {
        e.preventDefault();
        filterRef.current.resetForm();
        setIsFiltering(false);
        const payload = getCategories();
        setResults(defaultResults);
        dispatch(getCategoriesAsync(payload));
    };

    useEffect(() => {
        if (categories?.categories.status === 'idle') {
            dispatch(getCategoriesAsync());
        }
        if (categories?.categories.status === 'attempted') {
            setResults(categories?.categories?.results);
            setLoading(false);
        }
        if (companies?.companies.status === 'idle') {
            dispatch(getCompaniesAsync());
        }
    }, [categories, setResults, dispatch, companies?.companies.status]);

    const buttonStyles = {
        marginX: 'auto',
        width: 'fit-content',
        height: 'auto',
        alignItems: 'center',
        justifyContent: 'center',
        py: '14px',
        px: '24px',
        lineHeight: 1
    };

    const handleShowMore = () => {
        if (categories.categories?.meta?.current_page >= categories.categories?.meta?.last_page) {
            return;
        }

        setLiveFilters((prev) => ({
            ...prev,
            page: (categories.categories?.meta?.current_page + 1)
        }));
        const newFilters = { ...liveFilters, page: (categories.categories?.meta?.current_page + 1) };
        const payload = getCategories({ params: newFilters });
        setResults(() => defaultResults);
        setLoading(true);
        dispatch(appendCategoriesAsync(payload));
    }

    return (
        <DashboardLayout dark={true} hideCta={true} p={0}>
            <PageBanner img={'industries'} sx={{ paddingBottom: 0 }}>
                <Badge mt={4} px={3} variant="header" >{t('Stay ahead with Liquidity')}</Badge>
                <Heading mt={3} mb={2} as="h2" variant="industry_page_title">{t('Uncover the Latest Trends Shaping Tomorrow\'s Markets')}</Heading>

                <Box as="p" mt={3} mx={[1, "auto"]} sx={{ textAlign: 'center', color: 'darker' }}>
                    Explore in-depth industry insights and discover the categories driving innovation in our portfolio.
                </Box>

                <Box variant="layouts.default" mt={2}>
                    <Filters handleSubmit={handleSubmit} formRef={filterRef} />
                </Box>
            </PageBanner>
            <Container sx={{ position: 'relative', zIndex: 1 }} variant="layouts.default">
                {/* Industries cards */}
                <Flex ref={categoriesRef} mb={4} sx={{
                    flexDirection: 'column',
                    position: 'relative',
                }}>
                    {results?.length > 0 ?
                        <>
                            <Grid sx={{
                                gridGap: 4,
                                gridTemplateColumns: ['1fr', 'repeat(2, 1fr)', 'repeat(3, 1fr)'],
                                gridTemplateRows: '1fr',
                                width: '100%',
                                overflowX: 'auto',
                                pt: '10px',
                                pb: '30px',
                                px: 3,
                                mx: -3,
                                boxSizing: 'content-box',
                            }}>
                                {results.map((category, key) => (
                                    <Box key={`category-result${key}`}>
                                        <CompanyCard industry={category} />
                                    </Box>
                                ))}
                            </Grid>

                            {(!loading && results?.length < categories?.categories?.meta?.total) && 
                                <Button onClick={handleShowMore} variant="primary" sx={buttonStyles} mr={3} >{t('Show me more')}</Button>
                            }
                        </>
                    : null}

                    {(!loading && results?.length === 0) &&
                        <Box px={2}>
                            <Flex sx={{
                                py: 5,
                                alignItems: 'center',
                                justifyContent: 'center',
                                flexDirection: 'column'
                            }}>
                                <Icon icon={isFiltering ? 'error' : 'lock-thin'} color="tealDark" mb={'28px'} width="84px" />
                                <Heading variant="h2" sx={{ fontSize: '36px' }}>{t('categoriesDashboard.notFoundHeading')}</Heading>
                                <Text mt={1} variant="subtext">{isFiltering ? t('categoriesDashboard.filteringNotFound') : t('categoriesDashboard.notFound')}</Text>
                                {isFiltering &&
                                    <Button
                                        as={NavLink}
                                        onClick={resetFilters}
                                        sx={{
                                            minWidth: '160px',
                                            mt: '56px'
                                        }}
                                    >
                                        Reset filters
                                    </Button>
                                }
                            </Flex>
                        </Box>
                    }
                </Flex>

                {/* Companies Carousel */}
                <Flex sx={{ mt: 6, mb: 3, pb: 1 }}>                    
                    <Heading as="h1" variant="h3">{t('Explore Companies')}</Heading>
                    <Box ml={'auto'} mr={4}>
                        <DiscoverMore to={'/companies' } />
                    </Box>
                </Flex>
                <CompaniesCarousel />
            </Container>
        </DashboardLayout>
    );
};

export default connect(mapStateToProps, null)(IndustriesDashboard);